import Alert from '@mui/material/Alert';
import { useAuthentication } from '@src/authentication/useAuthentication';
import { isClientUser } from '@src/clientUser/useClientUser';
import { Checkbox } from '@src/common/components/Checkbox';
import { IconButton } from '@src/common/components/IconButton';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@src/common/components/List';
import { Stack } from '@src/common/components/Stack';
import { ViewableDocumentShortcut } from '@src/common/components/ViewableDocumentShortcut';
import { Dropzone } from '@src/common/Dropzone';
import { DownloadService } from '@src/common/services/DownloadService';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { FormFileModes } from '@src/documents/components/FormFileModes';
import { RenamingOption } from '@src/documents/components/RenamingOption';
import { DocumentsClient } from '@src/documents/services/DocumentsClient';
import { DocumentsFields } from '@src/permits/completePermit/fields/CompletePermitFields';
import { Download01, Trash01 } from '@untitled-ui/icons-react';
import { type FileWithPath, useDropzone } from 'react-dropzone';
import type { Control, FieldArrayWithId, FieldValues } from 'react-hook-form';
import { Controller, type Path, type PathValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface UploadFile {
  documentId: string;
  name: string;
  sendToClient: boolean;
  showToClient?: boolean;
  displayName?: string;
}

export type WithDocuments = FieldValues & {
  documents?: UploadFile[];
};

interface FormFileProps<T extends WithDocuments> {
  onDrop: (acceptedFiles: FileWithPath[]) => void;
  onDelete: ({ index, id }: { index: number; id?: string }) => void;
  onCheck: ({ checked, id }: { checked: boolean; id?: string }) => void;
  onRename: ({ name, id }: { name?: string; id?: string }) => void;
  control: Control<T>;
  fields: T extends WithDocuments
    ? FieldArrayWithId<WithDocuments, 'documents'>[]
    : never;
  readOnly?: boolean;
  mode: FormFileModes;
  isSmall?: boolean;
}

const download = async (fileId: string) => {
  const { blob, filename } = await DocumentsClient.downloadWithId(fileId);
  void DownloadService.download(blob, filename);
};

export const FormFileUpload = <T extends WithDocuments>({
  onDrop,
  onDelete,
  onCheck,
  onRename,
  control,
  fields,
  readOnly,
  mode,
  isSmall,
}: FormFileProps<T>) => {
  const { t } = useTranslation();
  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => onDrop(acceptedFiles),
    disabled: readOnly,
  });

  return (
    <Stack>
      {!readOnly && (
        <Dropzone
          isSmall={isSmall || fields.length > 0}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          open={open}
          fileExtensionsMessage={t('fileExtensions')}
        />
      )}

      {fields.length > 0 && (
        <Stack
          direction="column"
          gap={theme.spacing['3']}
          marginTop={theme.spacing['4']}
        >
          {!readOnly && (
            <Alert severity="warning">
              {mode === FormFileModes.SENT_TO_CLIENT
                ? t('sendFileToClientWarning')
                : t('displayFileToClientWarning')}
            </Alert>
          )}
          <List>
            {fields.map((item, index) => {
              const isCheckedReadOnly =
                mode === FormFileModes.MAKE_VISIBLE_TO_CLIENT &&
                item.sendToClient;
              const baseValue =
                mode === FormFileModes.SENT_TO_CLIENT
                  ? item.sendToClient
                  : item.showToClient;
              const fieldValue = isCheckedReadOnly || baseValue;

              return (
                <FileItem
                  documentFieldPath={`${DocumentsFields.fieldName}.${index}`}
                  key={item.id}
                  index={index}
                  file={item}
                  onDelete={onDelete}
                  onRename={onRename}
                  onCheck={onCheck}
                  control={control}
                  readOnly={readOnly}
                  fieldValue={fieldValue}
                  isCheckedReadOnly={isCheckedReadOnly}
                />
              );
            })}
          </List>
        </Stack>
      )}
    </Stack>
  );
};

export const FileItem = <T extends WithDocuments>({
  file,
  onDelete,
  onCheck,
  onRename,
  index,
  control,
  readOnly,
  documentFieldPath,
  fieldValue,
  isCheckedReadOnly,
}: {
  file: UploadFile;
  onDelete: ({ index, id }: { index: number; id?: string }) => void;
  onCheck: ({ checked, id }: { checked: boolean; id?: string }) => void;
  onRename: ({ name, id }: { name?: string; id?: string }) => void;
  index: number;
  control: Control<T>;
  readOnly?: boolean;
  documentFieldPath: string;
  fieldValue?: boolean;
  isCheckedReadOnly?: boolean;
}) => {
  const { t } = useTranslation();
  const { user } = useAuthentication();

  const downloadDocument = () => {
    if (file.documentId) void download(file.documentId);
  };

  const currentFile = { key: file.name, id: file.documentId };
  return (
    <ListItem className={item}>
      <ListItemIcon>
        <Stack direction="row">
          {!isClientUser(user) && (
            <Controller
              name={`${documentFieldPath}.check` as Path<T>}
              control={control}
              defaultValue={fieldValue as PathValue<T, Path<T>>}
              render={({ field: { value, ...field } }) => (
                <Checkbox
                  {...field}
                  checked={value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    onCheck({ id: file.documentId, checked: e.target.checked });
                  }}
                  label=""
                  disabled={readOnly || isCheckedReadOnly}
                />
              )}
            />
          )}
          <ViewableDocumentShortcut document={currentFile} fontSize="large" />
        </Stack>
      </ListItemIcon>
      <ListItemText
        inset
        primary={file.displayName || file.name}
        primaryTypographyProps={{
          sx: { fontWeight: 500 },
        }}
      />
      {!readOnly && (
        <IconButton
          title={t('delete')}
          onClick={() => onDelete({ id: file.documentId, index })}
        >
          <Trash01 />
        </IconButton>
      )}
      <IconButton title={t('download')} onClick={downloadDocument}>
        <Download01 />
      </IconButton>
      {!readOnly && (
        <RenamingOption
          documentId={file.documentId}
          currentName={file.displayName}
          onSubmit={(newName) =>
            onRename({ id: file.documentId, name: newName })
          }
        ></RenamingOption>
      )}
    </ListItem>
  );
};

const item = css(() => ({
  margin: '9px 0px',
}));
